import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { X } from 'lucide-react';

import { Mascot, ModalShell } from '../../components';

/**
 * 의견 남기기 안내 팝업(시안 `의견 남기기 #안내 팝업 노출`) — 첫 진단 결과를 들여다본
 * 사람이 홈에 돌아오면 한 번 뜬다. 누구에게·언제는 `HomePage` 와 `core/feedbackPrompt` 가
 * 정하고, 여기는 그리기만 한다.
 *
 * X·`다음에 할게요`·배경·Esc 는 전부 닫기다. 닫아도 MY 에 같은 화면으로 가는 줄이 있다.
 *
 * 포커스는 CTA 가 아니라 **제목**으로 옮긴다 — 사용자가 연 팝업이 아니라 저절로 뜨는
 * 팝업이라, CTA 가 포커스를 가져가면 치던 Enter 가 의견 화면으로 넘기고 열리자마자 포커스
 * 링이 선다(시안에 없다). 그렇다고 안 옮기면 키보드·스크린리더 사용자는 팝업이 뜬 줄 모른다.
 */
export const FeedbackPromptDialog = ({
  onAccept,
  onDismiss,
}: {
  onAccept: () => void;
  onDismiss: () => void;
}) => {
  const { t } = useTranslation();
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  useEffect(() => {
    titleRef.current?.focus();
  }, []);
  return (
    <ModalShell
      labelledBy="feedback-prompt-title"
      dismissLabel={t('web.feedback.close')}
      onDismiss={onDismiss}
      className="px-5 pb-4 pt-14"
    >
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('web.feedback.close')}
        className="absolute right-2 top-2 flex h-11 w-11 items-center justify-center text-bk-900"
      >
        <X className="h-6 w-6" aria-hidden="true" />
      </button>
      <Mascot className="mx-auto w-[96px]" />
      <h2
        id="feedback-prompt-title"
        ref={titleRef}
        tabIndex={-1}
        className="mt-6 whitespace-pre-line outline-none text-xl font-bold leading-snug tracking-[-0.2px] text-bk-900"
      >
        {t('web.feedback.prompt.title')}
      </h2>
      <p className="mt-3 whitespace-pre-line break-keep text-sm leading-relaxed text-bk-600">
        {t('web.feedback.prompt.body')}
      </p>
      <button
        type="button"
        onClick={onAccept}
        className="mt-8 flex h-[54px] w-full items-center justify-center rounded-full bg-bk-900 text-base font-bold text-white"
      >
        {t('web.feedback.prompt.cta')}
      </button>
      <button
        type="button"
        onClick={onDismiss}
        className="mt-1 flex h-11 w-full items-center justify-center text-[15px] font-medium text-bk-900"
      >
        {t('web.feedback.prompt.later')}
      </button>
    </ModalShell>
  );
};
